import React from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import {Typography, Button} from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Colors from "../../components/ui/Colors";
import { PuffLoader } from "react-spinners";
import { css } from "@emotion/react";
import swal from 'sweetalert';
import { useHistory, useLocation } from "react-router-dom";
import Pagination from '@mui/material/Pagination';

const cssLoader = css`
  display: block;
  margin-left: auto;
  margin-right: auto;
`;

const dateOptions = {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
};

export default function ManualInventoryEntryList(props) {
    const {loginUser} = props;
    const location = useLocation();
    const history = useHistory();

    const [loading, setLoading] = React.useState(false);
    const [entryList, setEntryList] = React.useState([]);
    const [manualInventory, setManualInventory] = React.useState(null);
    const [currentPage, setCurrentPage] = React.useState(0);
    const [totalPages, setTotalPages] = React.useState(1);
    const [pageSize] = React.useState(30);

    React.useEffect(() => {
        if (location.state && location.state.manualInventory) {
            setManualInventory(location.state.manualInventory);
            processInventoryData(location.state.manualInventory);
        } else {
            // No inventory provided, show error
            swal({
                title: "Error",
                text: "No se proporcionó el inventario manual",
                icon: "error",
                buttons: "OK"
            }).then(() => {
                history.push("/warehouse/manual-inventory-list");
            });
        }
    }, [location.state]);

    const processInventoryData = (inventory) => {
        setLoading(true);
        
        try {
            const entries = inventory.batchEntries || [];
            
            // Sort entries by date if needed
            const sortedEntries = [...entries].sort((a, b) => {
                // Sort by creation date if available, newest first
                if (a.instantDate && b.instantDate) {
                    return new Date(b.instantDate) - new Date(a.instantDate);
                }
                return 0;
            });
            
            setEntryList(sortedEntries);
            setTotalPages(Math.ceil(sortedEntries.length / pageSize));
            
            // Process pagination manually since we have all data
            const start = currentPage * pageSize;
            const end = start + pageSize;
            const pagedEntries = sortedEntries.slice(start, end);
            
            renderEntryList(pagedEntries);
        } catch (error) {
            console.error("Error processing inventory data:", error);
            swal("Error", "Error al procesar los datos del inventario", "error");
            setEntryList([]);
            renderEntryList([]);
        } finally {
            setLoading(false);
        }
    };

    const [entryListUI, setEntryListUI] = React.useState("");

    const renderEntryList = (data) => {
        if (!data || data.length === 0) {
            setEntryListUI(
                <Typography variant="body1" sx={{ marginTop: "2rem" }}>
                    No hay entradas disponibles para este inventario manual
                </Typography>
            );
            return;
        }

        setEntryListUI(
            <List sx={{ width: "100%", marginTop: { xs: "3rem", sm: "2rem", md: "2rm" } }}>
                {data.map((entry) => {
                    const createdDate = entry.instantDate ? new Date(entry.instantDate) : new Date();
                    const productDescription = entry.description || 'N/A';
                    const systemAmount = entry.systemProductAmount || 0;
                    
                    return (
                        <ListItem key={entry.id} disablePadding divider sx={{ marginBottom: "1.5rem" }}>
                            <ListItemButton>
                                <Grid container spacing={1}>
                                    <Grid item xs={2} sm={2} md={2} sx={{ display: "flex", justifyContent: "center" }}>
                                        <Typography variant="body2">{`${new Intl.DateTimeFormat('es-CO', dateOptions).format(createdDate)}`}</Typography>
                                    </Grid>
                                    <Grid item xs={2} sm={2} md={2} sx={{ display: "flex", justifyContent: "center" }}>
                                        <Typography variant="body2">{entry.batchId}</Typography>
                                    </Grid>
                                    <Grid item xs={2} sm={2} md={2} sx={{ display: "flex", justifyContent: "center" }}>
                                        <Typography variant="body2">{productDescription}</Typography>
                                    </Grid>
                                    <Grid item xs={1} sm={1} md={1} sx={{ display: "flex", justifyContent: "center" }}>
                                        <Typography variant="body2">{new Intl.NumberFormat('es-CO').format(systemAmount)}</Typography>
                                    </Grid>
                                    <Grid item xs={1} sm={1} md={1} sx={{ display: "flex", justifyContent: "center" }}>
                                        <Typography variant="body2">{new Intl.NumberFormat('es-CO').format(entry.manualProductAmount)}</Typography>
                                    </Grid>
                                    <Grid item xs={1} sm={1} md={1} sx={{ display: "flex", justifyContent: "center" }}>
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                color: entry.difference === 0 ? 'black' : (entry.difference > 0 ? 'green' : 'red')
                                            }}
                                        >
                                            {entry.difference > 0 ? '+' : ''}{new Intl.NumberFormat('es-CO').format(entry.difference)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={2} sm={2} md={2} sx={{ display: "flex", justifyContent: "center" }}>
                                        <Typography variant="body2">{entry.comment}</Typography>
                                    </Grid>
                                    <Grid item xs={1} sm={1} md={1} sx={{ display: "flex", justifyContent: "center" }}>
                                        <Typography 
                                            variant="body2"
                                            sx={{ 
                                                color: entry.status === 'ok' ? 'green' : 'orange'
                                            }}
                                        >
                                            {entry.status === 'ok' ? 'OK' : 'Investigando'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>
        );
    };

    const handlePageChange = (event, value) => {
        setCurrentPage(value - 1); // MUI Pagination is 1-indexed
        
        if (manualInventory && manualInventory.batchEntries) {
            const entries = manualInventory.batchEntries || [];
            const start = (value - 1) * pageSize;
            const end = start + pageSize;
            const pagedEntries = entries.slice(start, end);
            renderEntryList(pagedEntries);
        }
    };

    const addNewEntry = () => {
        history.push({
            pathname: "/warehouse/manual-inventory-entry",
            state: {
                manualInventoryId: manualInventory ? manualInventory.id : null
            }
        });
    };

    const goBack = () => {
        history.push({
            pathname: "/warehouse/manual-inventory-list"
        });
    };

    return (
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%", height: "100%" }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: { xs: "start", sm: "center" },
                    width: "90%",
                    height: "100%",
                    textAlign: "center",
                    overflowY: "scroll",
                }}
            >
                {loading ? <PuffLoader size={100} color={Colors.secondary} css={cssLoader} /> : 
                <>
                    <Grid container>
                        {manualInventory && (
                            <Grid item xs={12} sm={12} md={12} sx={{ marginTop: "1.5rem" }}>
                                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                                    Inventario Manual #{manualInventory.id}
                                </Typography>
                                <Typography variant="body1">
                                    Creado por: {manualInventory.username} el {new Intl.DateTimeFormat('es-CO', dateOptions).format(new Date(manualInventory.instantDate))}
                                </Typography>
                            </Grid>
                        )}
                        
                        <Grid item xs={12} sm={12} md={12} sx={{ display: "flex", justifyContent: "center", marginTop: "1.5rem", gap: 2 }}>
                            <Button 
                                variant="contained" 
                                onClick={goBack}
                                sx={{ backgroundColor: Colors.primary, marginBottom: "1rem" }}
                            >
                                Volver
                            </Button>
                            <Button 
                                variant="contained" 
                                onClick={addNewEntry}
                                sx={{ backgroundColor: Colors.primary, marginBottom: "1rem" }}
                            >
                                Agregar Nueva Entrada
                            </Button>
                        </Grid>
                        
                        <Grid item xs={12} sm={12} md={12}>
                            <Grid container sx={{ marginTop: "1.5rem" }}>
                                <Grid item xs={2} sm={2} md={2} sx={{ display: "flex", justifyContent: "center" }}>
                                    <Typography variant="body1" sx={{ color: "black", fontWeight: "bold", textDecoration: 'underline' }}>Fecha</Typography>
                                </Grid>
                                <Grid item xs={2} sm={2} md={2} sx={{ display: "flex", justifyContent: "center" }}>
                                    <Typography variant="body1" sx={{ color: "black", fontWeight: "bold", textDecoration: 'underline' }}>Id Estiba</Typography>
                                </Grid>
                                <Grid item xs={2} sm={2} md={2} sx={{ display: "flex", justifyContent: "center" }}>
                                    <Typography variant="body1" sx={{ color: "black", fontWeight: "bold", textDecoration: 'underline' }}>Producto</Typography>
                                </Grid>
                                <Grid item xs={1} sm={1} md={1} sx={{ display: "flex", justifyContent: "center" }}>
                                    <Typography variant="body1" sx={{ color: "black", fontWeight: "bold", textDecoration: 'underline' }}>Sistema</Typography>
                                </Grid>
                                <Grid item xs={1} sm={1} md={1} sx={{ display: "flex", justifyContent: "center" }}>
                                    <Typography variant="body1" sx={{ color: "black", fontWeight: "bold", textDecoration: 'underline' }}>Manual</Typography>
                                </Grid>
                                <Grid item xs={1} sm={1} md={1} sx={{ display: "flex", justifyContent: "center" }}>
                                    <Typography variant="body1" sx={{ color: "black", fontWeight: "bold", textDecoration: 'underline' }}>Dif.</Typography>
                                </Grid>
                                <Grid item xs={2} sm={2} md={2} sx={{ display: "flex", justifyContent: "center" }}>
                                    <Typography variant="body1" sx={{ color: "black", fontWeight: "bold", textDecoration: 'underline' }}>Comentario</Typography>
                                </Grid>
                                <Grid item xs={1} sm={1} md={1} sx={{ display: "flex", justifyContent: "center" }}>
                                    <Typography variant="body1" sx={{ color: "black", fontWeight: "bold", textDecoration: 'underline' }}>Estado</Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        
                        <Grid item xs={12} sm={12} md={12}>
                            {entryListUI}
                        </Grid>
                        
                        <Grid item xs={12} sm={12} md={12} sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 2 }}>
                            <Pagination 
                                count={totalPages} 
                                page={currentPage + 1} 
                                onChange={handlePageChange} 
                                color="primary" 
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={12} sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 2 }}>
                            <Button 
                                variant="contained" 
                                onClick={() => history.push("/warehouse/manual-inventory-list")}
                                sx={{ backgroundColor: Colors.primary, marginBottom: "1rem" }}
                            >
                                Atrás
                            </Button>
                        </Grid>
                    </Grid>
                </>
                }
            </Box>
        </Box>
    );
} 